import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { PageTitle, Loading, Tooltip, Modal } from '../root/components/common'
import { NavLink, withRouter } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import * as actions from './actions'
import { Table } from '../root/components/common/table'
import { getRoleName } from '../../utils'
import { UserPermissions } from '../../services/permissions'
import { Box, IconButton } from '@material-ui/core'
import { mdiPencilPlusOutline } from '@mdi/js'
import MdiIcon from '@mdi/react'
import { composeFileName } from '../../utils'
import { getFileForExport } from './api/users-api'
import { enqueueSnackbar } from '../root/actions'
import { LOCAL_LOCALE_KEY } from '../root/constants'
import { downloadFile } from '../../utils'
import { ExportMenu } from '../root/components/common/export-menu'
import DeleteIcon from '@material-ui/icons/DeleteOutline'
import { deleteCustomRolesAction } from './actions'
import { DeleteRoleModal } from './modals/delete-role-modal'
import { UsersAssignedRoleInfoModal } from './modals/users-assigned-role-info-modal'
import { DEFAULT_ROLES } from 'web/src/app/root/constants'
import { useQuery, useMutation } from 'react-query'
import { checkHasRoleUsers, reassignAndDeleteRole } from './api/api'
import { ReassignAndConfirmDeleteRoleModal } from './modals/delete-and-reassign-role-modal'

const useStyles = makeStyles(theme => ({
    wrapper: {
        height: 'calc(100% - 70px)'
    },
    link: {
        color: theme.textPrimary,
        textDecoration: 'none',
        fontWeight: 'bold',
        cursor: 'pointer'
    },
    pageTitle: {
        justifyContent: 'space-between'
    },
    actions: {
        display: 'flex',
        justifyContent: 'center'
    }
}))

function Roles(props) {
    const classes = useStyles()
    const dispatch = useDispatch()
    const { roles } = useSelector(state => state.userManagement)
    const { isLoading, locale, theme } = useSelector(state => state.main)

    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [
        usersAssignedRoleInfoModalOpen,
        setUsersAssignedRoleInfoModalOpen
    ] = useState(false)
    const [
        reassignAndConfirmDeleteRoleModalOpen,
        setReassignAndConfirmDeleteRoleModalOpen
    ] = useState(false)

    const [roleId, setRoleId] = useState(null)
    const [hasRoleUsers, setHasRoleUsers] = useState(true)

    const { isLoading: isLoadingCheckRole } = useQuery(
        [roleId],
        () => checkHasRoleUsers({ roleId }),
        {
            refetchOnWindowFocus: false,
            enabled: !!roleId,
            onSuccess: data => {
                setHasRoleUsers(data.hasUsers)
            },
            cacheTime: 0,
            onError: () => {
                setDeleteModalOpen(false)
                setReassignAndConfirmDeleteRoleModalOpen(false)
                dispatch(
                    enqueueSnackbar({
                        message: locale.sagasMsgs.commonLoadingError,
                        options: {
                            variant: 'error'
                        }
                    })
                )
            }
        }
    )

    const {
        mutate: reassignAndDeleteRoleMutation,
        isLoading: isLoadingReassignAndDeleteRole
    } = useMutation('reassign-and-delete-role', reassignAndDeleteRole, {
        onSuccess: () => {
            setReassignAndConfirmDeleteRoleModalOpen(false)
            dispatch(actions.rolesRequest())
            enqueueSnackbar({
                message: locale.sagasMsgs.deleteRoleRes,
                options: {
                    variant: 'success'
                }
            })
        },
        onError: () => {
            dispatch(
                enqueueSnackbar({
                    message: locale.sagasMsgs.commonLoadingError,
                    options: {
                        variant: 'error'
                    }
                })
            )
        }
    })

    const onDeleteRoleClick = () => {
        if (!hasRoleUsers) {
            dispatch(
                deleteCustomRolesAction({
                    roleId,
                    setUsersAssignedRoleInfoModalOpen
                })
            )
            setRoleId(null)
            setDeleteModalOpen(false)
            return
        }

        setDeleteModalOpen(false)
        setReassignAndConfirmDeleteRoleModalOpen(true)
    }

    const onConfirmDeleteRoleAndUsersMove = reassignRoleId => {
        reassignAndDeleteRoleMutation({ roleId, reassignRoleId })
    }

    const columns = [
        {
            key: 'name',
            label: locale.common.name,
            sortable: true,
            dataType: 'string',
            useCustomRenderer: true
        },
        {
            key: 'customer_name',
            label: locale.common.organisation,
            sortable: true,
            dataType: 'string',
            useCustomRenderer: false
        },
        {
            key: 'actions',
            label: locale.userManagement.actions,
            sortable: false,
            dataType: 'custom',
            width: 1.5,
            alignCenter: true,
            useCustomRenderer: true
        }
    ]

    const exportRoles = async fileType => {
        const locale_key = localStorage.getItem(LOCAL_LOCALE_KEY)
        const params = { fileType, locale_key }
        params.roleExport = true
        const blob = await getFileForExport(params)
        if (blob) {
            const fileName = composeFileName('roles', fileType)
            downloadFile(blob, fileType, fileName)
        } else {
            dispatch(
                enqueueSnackbar({
                    message: locale.sagasMsgs.getDevicesEx,
                    options: {
                        variant: 'error'
                    }
                })
            )
        }
    }

    const tableCustomRenderer = ({ rowData, cellData, columnKey }) => {
        switch (columnKey) {
            case 'name':
                return (
                    <NavLink
                        className={classes.link}
                        to={`/user-management/roles/${rowData.id}`}
                    >
                        {getRoleName(rowData, locale)}
                    </NavLink>
                )
            case 'actions':
                return (
                    <Box className={classes.actions}>
                        <Tooltip
                            hover
                            placement="top"
                            text={
                                !DEFAULT_ROLES[rowData.code]
                                    ? ''
                                    : locale.userManagement
                                          .youCanNotDeleteThisRole
                            }
                        >
                            <IconButton
                                size="small"
                                disabled={DEFAULT_ROLES[rowData.code]}
                                onClick={() => {
                                    setRoleId(rowData.id)
                                    setDeleteModalOpen(true)
                                }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )
        }
    }

    const goToNewRole = () => {
        props.history.push('/user-management/roles/new')
    }

    useEffect(() => {
        dispatch(actions.rolesRequest())
        return () => {
            dispatch(actions.rolesRequestSuccess(null))
        }
    }, [])

    return (
        <div className={classes.wrapper}>
            <PageTitle
                title={locale.userManagement.roles}
                className={classes.pageTitle}
            >
                <ExportMenu>
                    {({ handleClose }) => (
                        <>
                            <ExportMenu.Csv
                                locale={locale}
                                onExport={fileType => {
                                    exportRoles(fileType)
                                    handleClose()
                                }}
                            />
                            <ExportMenu.Excel
                                locale={locale}
                                onExport={fileType => {
                                    exportRoles(fileType)
                                    handleClose()
                                }}
                            />
                            <ExportMenu.Pdf
                                locale={locale}
                                onExport={fileType => {
                                    exportRoles(fileType)
                                    handleClose()
                                }}
                            />
                        </>
                    )}
                </ExportMenu>
                {UserPermissions.user().create && (
                    <Tooltip
                        hover
                        placement="top"
                        text={locale.userManagement.createNew}
                    >
                        <IconButton onClick={goToNewRole}>
                            <MdiIcon
                                path={mdiPencilPlusOutline}
                                size={1}
                                color={theme.quaternary}
                            />
                        </IconButton>
                    </Tooltip>
                )}
            </PageTitle>
            {isLoading && !roles && <Loading />}
            {!isLoading && roles && roles.length > 0 && (
                <Table
                    rows={roles.map((role, idx) => ({
                        idx: idx + 1,
                        ...role
                    }))}
                    dataUniqueKey="idx"
                    columns={columns}
                    customRenderer={tableCustomRenderer}
                />
            )}
            {!isLoading && (!roles || roles.length === 0) && (
                <div>{locale.userManagement.noRolesFound}</div>
            )}
            <Modal open={deleteModalOpen} setOpen={setDeleteModalOpen}>
                <DeleteRoleModal
                    setOpen={setDeleteModalOpen}
                    onDeleteRoleClick={onDeleteRoleClick}
                    isLoading={isLoadingCheckRole}
                />
            </Modal>

            <Modal
                open={usersAssignedRoleInfoModalOpen}
                setOpen={setUsersAssignedRoleInfoModalOpen}
            >
                <UsersAssignedRoleInfoModal
                    setOpen={setUsersAssignedRoleInfoModalOpen}
                />
            </Modal>

            <Modal
                open={reassignAndConfirmDeleteRoleModalOpen}
                setOpen={setReassignAndConfirmDeleteRoleModalOpen}
            >
                <ReassignAndConfirmDeleteRoleModal
                    isLoading={isLoadingReassignAndDeleteRole}
                    roleId={roleId}
                    onClose={() =>
                        setReassignAndConfirmDeleteRoleModalOpen(false)
                    }
                    onConfirmDelete={onConfirmDeleteRoleAndUsersMove}
                />
            </Modal>
        </div>
    )
}

export default withRouter(Roles)
